import { useRef, useState } from "react";
import { Modal } from "./ui";
import { BACKEND_URL, api } from "../lib/api";
import { Upload, Check, AlertTriangle, FileSpreadsheet, FileText as FileTextIcon, ArrowRight } from "lucide-react";

/**
 * Generic preview→map→confirm import flow. Supports .xlsx, .xls and .pdf with
 * selectable text. Used by CrudTable in every module.
 *
 * Props:
 *  - collection (string)            target MongoDB collection
 *  - fields (array)                 column schema {key, label, type, options?}
 *  - parent { id, field } (optional) auto-fills parent_id on import
 *  - open / onClose / onImported
 */
export default function UniversalImport({
  collection,
  fields = [],
  parent,
  open,
  onClose,
  onImported,
}) {
  const fileRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [rows, setRows] = useState([]);
  const [filename, setFilename] = useState("");
  const [error, setError] = useState("");

  const targetOptions = fields
    .filter((f) => !f.hidden && f.key !== "id" && f.key !== "tenant_id")
    .map((f) => ({ key: f.key, label: f.label }));

  function reset() {
    setPreview(null);
    setMapping({});
    setRows([]);
    setFilename("");
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleFile(file) {
    if (!file) return;
    setError("");
    setAnalyzing(true);
    setPreview(null);
    setRows([]);
    setFilename(file.name);
    const form = new FormData();
    form.append("file", file);
    const token = localStorage.getItem("token");
    const audit = localStorage.getItem("audit_tenant_id");
    const headers = { Authorization: `Bearer ${token}` };
    if (audit) headers["X-Audit-Tenant-Id"] = audit;
    const allowed = targetOptions.map((t) => t.key).join(",");
    const previewUrl = `${BACKEND_URL}/api/import/${collection}/preview?allowed_fields=${encodeURIComponent(allowed)}`;
    const aiUrl = `${BACKEND_URL}/api/import/${collection}/ai-extract?allowed_fields=${encodeURIComponent(allowed)}`;
    try {
      let res = await fetch(previewUrl, { method: "POST", headers, body: form });
      // If the regular parser cannot read the PDF, retry via AI Vision extraction
      if (res.status === 422) {
        setError("📷 PDF escaneado detectado. Extrayendo con IA (Claude Vision)...");
        const form2 = new FormData();
        form2.append("file", file);
        res = await fetch(aiUrl, { method: "POST", headers, body: form2 });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || `Error ${res.status}`);
        return;
      }
      const data = await res.json();
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setRows(data.rows);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalyzing(false);
    }
  }

  function updateMapping(srcHeader, targetKey) {
    setMapping({ ...mapping, [srcHeader]: targetKey });
  }
  function updateCell(rowIdx, header, value) {
    const copy = [...rows];
    copy[rowIdx] = { ...copy[rowIdx], [header]: value };
    setRows(copy);
  }

  async function confirm() {
    setError("");
    setConfirming(true);
    try {
      const cleanMapping = Object.fromEntries(
        Object.entries(mapping).filter(([_, v]) => v)
      );
      const body = {
        mapping: cleanMapping,
        rows,
        source_type: preview?.source_type,
      };
      if (parent) {
        body.parent_field = parent.field;
        body.parent_id = parent.id;
      }
      const { data } = await api.post(`/import/${collection}/confirm`, body);
      alert(`Se importaron ${data.inserted} registros.`);
      if (onImported) onImported(data);
      reset();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setConfirming(false);
    }
  }

  const mappedCount = Object.values(mapping).filter((v) => v).length;
  const sourceIcon =
    preview?.source_type === "pdf" ? (
      <FileTextIcon size={14} />
    ) : (
      <FileSpreadsheet size={14} />
    );

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Importar datos (Excel / PDF)"
      size="xl"
      testId="universal-import-modal"
    >
      <div className="grid gap-3">
        <div
          className="card"
          style={{ background: "rgba(29,158,117,0.06)", border: "1px solid var(--accent)" }}
        >
          <div className="text-sm">
            Sube un archivo <code>.xlsx</code>, <code>.xls</code> o <code>.pdf</code>. El sistema
            detecta las columnas, sugiere el mapeo a los campos del módulo y te muestra una vista
            previa editable. Para PDFs escaneados (solo imagen) se activa automáticamente la
            extracción con IA (Claude Vision).
          </div>
        </div>

        <div>
          <label className="label">Archivo</label>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.pdf"
            className="input"
            onChange={(e) => handleFile(e.target.files[0])}
            data-testid="universal-import-file"
          />
          {filename && (
            <div className="text-xs text-dim mt-2">
              {sourceIcon} {filename}
            </div>
          )}
        </div>

        {analyzing && (
          <div className="text-dim text-sm flex items-center gap-2">
            <span className="spinner" /> Analizando archivo...
          </div>
        )}

        {error && (
          <div
            className="card"
            style={{ background: "rgba(231,76,60,0.10)", border: "1px solid var(--danger)" }}
            data-testid="universal-import-error"
          >
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {preview && (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="badge badge-green">
                <Check size={12} /> {preview.total_rows} filas detectadas
              </span>
              <span className="badge">{preview.source_type.toUpperCase()}</span>
              <span className="badge badge-blue">
                {mappedCount}/{preview.headers.length} columnas mapeadas
              </span>
              {preview.truncated && (
                <span className="badge badge-yellow">
                  <AlertTriangle size={12} /> Mostrando primeras 500 filas
                </span>
              )}
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">
                Mapeo de columnas (archivo → módulo)
              </div>
              <div className="grid grid-cols-2 gap-2">
                {preview.headers.map((h) => (
                  <div
                    key={h}
                    className="flex items-center gap-2"
                    style={{
                      background: "var(--bg-2)",
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid var(--border)",
                    }}
                    data-testid={`map-row-${h}`}
                  >
                    <div
                      className="text-xs"
                      style={{ flex: 1, fontWeight: 600, wordBreak: "break-word" }}
                    >
                      {h}
                    </div>
                    <ArrowRight size={12} className="text-dim" />
                    <select
                      className="select"
                      style={{ flex: 1.4, padding: "4px 6px", fontSize: 12 }}
                      value={mapping[h] || ""}
                      onChange={(e) => updateMapping(h, e.target.value)}
                      data-testid={`map-select-${h}`}
                    >
                      <option value="">— Ignorar columna —</option>
                      {targetOptions.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label} ({t.key})
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">
                Vista previa editable (haz click en cualquier celda para corregir)
              </div>
              <div className="table-wrap" style={{ maxHeight: 340, overflow: "auto" }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {preview.headers.map((h) => (
                        <th key={h}>
                          {h}
                          {mapping[h] && (
                            <div className="text-xs text-accent" style={{ fontWeight: 400 }}>
                              → {mapping[h]}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} data-testid={`preview-row-${i}`}>
                        <td>{i + 1}</td>
                        {preview.headers.map((h) => (
                          <td key={h} style={{ minWidth: 100 }}>
                            <input
                              className="input"
                              style={{ padding: 4, fontSize: 12 }}
                              value={r[h] ?? ""}
                              onChange={(e) => updateCell(i, h, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button className="btn" onClick={reset} data-testid="universal-import-reset">
                Cancelar / Cargar otro archivo
              </button>
              <button
                className="btn btn-primary"
                onClick={confirm}
                disabled={confirming || mappedCount === 0}
                data-testid="universal-import-confirm"
              >
                {confirming ? (
                  <span className="spinner" />
                ) : (
                  <>
                    <Upload size={14} /> Confirmar e importar {rows.length} filas
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
